import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { ensureCsrf, getCsrfToken } from '@/api/client'

const routes: RouteRecordRaw[] = [
  // ─── Auth (no layout) ───
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false, title: 'Login' },
  },
  {
    path: '/first-login/:token',
    name: 'enrollment',
    component: () => import('@/views/auth/EnrollmentView.vue'),
    meta: { requiresAuth: false, title: 'Enrollment' },
  },

  // ─── Authenticated (with AppLayout) ───
  {
    path: '/',
    component: () => import('@/components/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // User pages
      {
        path: '',
        name: 'home',
        component: () => import('@/views/home/HomeView.vue'),
        meta: { title: 'Home' },
      },
      {
        path: 'files',
        name: 'files',
        component: () => import('@/views/home/FilesView.vue'),
        meta: { title: 'Files' },
      },
      {
        path: 'privacy-policy',
        name: 'privacy-policy',
        component: () => import('@/views/PrivacyPolicyView.vue'),
        meta: { title: 'Privacy Policy' },
      },

      // Admin pages (local interface only)
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/UsersView.vue'),
        meta: { requiresAdmin: true, title: 'Users Management' },
      },
      {
        path: 'admin/files',
        name: 'admin-files',
        component: () => import('@/views/admin/FilesView.vue'),
        meta: { requiresAdmin: true, title: 'Files Management' },
      },
      {
        path: 'admin/usecases',
        name: 'admin-usecases',
        component: () => import('@/views/admin/UsecasesView.vue'),
        meta: { requiresAdmin: true, title: 'Usecases Management' },
      },
      {
        path: 'admin/groups',
        name: 'admin-groups',
        component: () => import('@/views/admin/GroupsView.vue'),
        meta: { requiresAdmin: true, title: 'Groups Management' },
      },
      {
        path: 'admin/logs',
        name: 'admin-logs',
        component: () => import('@/views/admin/LogsView.vue'),
        meta: { requiresAdmin: true, title: 'Logs' },
      },
      {
        path: 'admin/health',
        name: 'admin-health',
        component: () => import('@/views/admin/HealthView.vue'),
        meta: { requiresAdmin: true, title: 'Health' },
      },
    ],
  },

  // ─── Error pages ───
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { title: 'Not Found' },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guard
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Ensure session is loaded on first navigation
  if (!auth.user && !auth.loading) {
    await auth.fetchSession()
  }

  // Redirect to login if auth required and not authenticated
  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  // Ensure CSRF token is available for all authenticated requests
  await ensureCsrf()

  // Expose CSRF token in a hidden DOM input so test automation (page.evaluate fetch calls)
  // can read it — mirrors the hidden input that old Flask templates provided.
  const token = getCsrfToken()
  if (token) {
    let inp = document.getElementById('csrf-token-input') as HTMLInputElement | null
    if (!inp) {
      inp = document.createElement('input')
      inp.type = 'hidden'
      inp.id = 'csrf-token-input'
      inp.name = 'csrf_token'
      document.body.appendChild(inp)
    }
    inp.value = token
  }

  // Redirect to home if already authenticated and going to login
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: 'home' }
  }

  // Check admin access
  if (to.meta.requiresAdmin && !auth.isLocalInterface) {
    return { name: 'home' }
  }

  const siteTitle = auth.config?.siteTitle ?? 'Trinity'
  const pageTitle = typeof to.meta.title === 'string' ? to.meta.title : ''
  document.title = pageTitle ? `${siteTitle} portal - ${pageTitle}` : `${siteTitle} portal`
})

export default router
